/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { BranchesDaoService } from '../branches-dao.service';
import { Branch } from 'src/app/_model/branches';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { IMSId, LabelFormatSetting } from 'src/app/_model/ims-object';
import { BranchDbTableDefinition } from './branches-db-table-definition';
import { LocalBranchDbTableDefinition } from './local-branches-db-table-definition';

@Injectable()
export class BranchesDbDaoService implements BranchesDaoService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private appParameter: AppParameterService,
    private branchTableDefinition: BranchDbTableDefinition,
    private localBranchTableDefinition: LocalBranchDbTableDefinition,
  ) {}

  public async saveBranches(branches: Branch[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var branchQueries: Query[] = [];
      for (const branch of branches) {
        let branchQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (branch.isLocal()
              ? this.localBranchTableDefinition.tableName
              : this.branchTableDefinition.tableName) +
            '(id, label, databag, oin, defaultpersonid, ' +
            ' defaultpersonlabel, defaultpersonphone , defaultpersonemail, ' +
            ' locationid, locationlabel, customerid, inetaddress, ' +
            ' validmatchingradius, extid, validlongitude, ' +
            ' validlatitude , email, geodataisaddresspreciseormanual, ' +
            ' defaultpersonmobile, defaultpersonfax, name, shortname,' +
            ' phone, fax, defaultpersonprimaryphone, geodataismanual)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(branch.getImsId()),
            branch.label,
            branch.databag ? JSON.stringify(branch.databag) : undefined,
            branch.oin ? branch.oin : '',
            branch.defaultperson?.id?.imsId
              ? Number(branch.defaultperson.getImsId())
              : undefined,
            branch.defaultperson?.id?.imsId ? branch.defaultperson.label : '',
            branch.defaultpersonphone ? branch.defaultpersonphone : '',
            branch.defaultpersonemail ? branch.defaultpersonemail : '',
            branch.location?.id?.imsId ? Number(branch.location.getImsId()): undefined,
            branch.location?.id?.imsId ? branch.location.label : '',
            branch.customerid,
            branch.inetaddress ? branch.inetaddress : '',
            branch.validmatchingradius,
            branch.extid ? branch.extid : '',
            branch.validlongitude,
            branch.validlatitude,
            branch.email ? branch.email : '',
            branch.geodataisaddresspreciseormanual
              ? branch.geodataisaddresspreciseormanual
              : false,
            branch.defaultpersonmobile ? branch.defaultpersonmobile : '',
            branch.defaultpersonfax ? branch.defaultpersonfax : '',
            branch.name ? branch.name : '',
            branch.shortname ? branch.shortname : '',
            branch.phone ? branch.phone : '',
            branch.fax ? branch.fax : '',
            branch.defaultpersonprimaryphone
              ? branch.defaultpersonprimaryphone
              : '',
            branch.geodataismanual ?? false,
          ],
        };
        branchQueries.push(branchQuery);
      }

      await this.dbPersistenceService.executeBatch(db, branchQueries);
    } catch (error) {
      this.logger.log(
        'Error saving branches to db',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async createBranch(branch: Branch): Promise<Branch> {
    try {
      // local_branches has no autoincrement id column, so a unique local id
      // is generated client-side instead of relying on last_insert_rowid().
      if (branch.id.secondaryId == undefined) {
        branch.id.secondaryId = -Date.now();
      }
      await this.saveBranches([branch]);
      return branch;
    } catch (error) {
      this.logger.log(
        'Error creating local branch',
        error,
        '-',
        error?.message,
      );
      return undefined;
    }
  }

  public async deleteLocalBranch(id: IMSId): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' + this.localBranchTableDefinition.tableName + ' WHERE id = (?);',
        parameters: [id.secondaryId],
      });
    } catch (error) {
      this.logger.log('Error deleting local branch', id, 'from db', error);
    }
  }

  public async getBranches(): Promise<Branch[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let branches: Branch[] = [];

    // Get Synced Branches - isolated try/catch so a problem reading the
    // local table can never wipe out the already-loaded synced ones.
    try {
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.branchTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        branches.push(
          Branch.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              oin: row['oin'],
              defaultperson: {
                id: row['defaultpersonid'],
                label: row['defaultpersonlabel'],
              },
              defaultpersonphone: row['defaultpersonphone'],
              defaultpersonprimaryphone: row['defaultpersonprimaryphone'],
              defaultpersonmobile: row['defaultpersonmobile'],
              defaultpersonemail: row['defaultpersonemail'],
              defaultpersonfax: row['defaultpersonfax'],
              location: {
                id: row['locationid'],
                label: row['locationlabel'],
              },
              customerid: row['customerid'],
              inetaddress: row['inetaddress'],
              validmatchingradius: row['validmatchingradius'],
              extid: row['extid'],
              validlongitude: row['validlongitude'],
              validlatitude: row['validlatitude'],
              email: row['email'],
              geodataisaddresspreciseormanual:
                row['geodataisaddresspreciseormanual'] === 1,
              geodataismanual: row['geodataismanual'] === 1,
              name: row['name'],
              shortname: row['shortname'],
              phone: row['phone'],
              fax: row['fax'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/Branch'),
          ),
        );
      }
    } catch (error) {
      this.logger.log('Error reading synced branches from db', error);
    }

    // Get locally created branches (not yet on the server) - isolated
    // try/catch so a problem here can never wipe out the synced ones above.
    try {
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localBranchTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        let row = localData.values[i];
        branches.push(
          Branch.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              label: row['label'],
              oin: row['oin'],
              defaultperson: row['defaultpersonid']
                ? {
                    id: row['defaultpersonid'],
                    label: row['defaultpersonlabel'],
                  }
                : undefined,
              defaultpersonphone: row['defaultpersonphone'],
              defaultpersonprimaryphone: row['defaultpersonprimaryphone'],
              defaultpersonmobile: row['defaultpersonmobile'],
              defaultpersonemail: row['defaultpersonemail'],
              defaultpersonfax: row['defaultpersonfax'],
              location: {
                id: row['locationid'],
                label: row['locationlabel'],
              },
              customerid: row['customerid'],
              inetaddress: row['inetaddress'],
              validmatchingradius: row['validmatchingradius'],
              extid: row['extid'],
              validlongitude: row['validlongitude'],
              validlatitude: row['validlatitude'],
              email: row['email'],
              geodataisaddresspreciseormanual:
                row['geodataisaddresspreciseormanual'] === 1,
              geodataismanual: row['geodataismanual'] === 1,
              name: row['name'],
              shortname: row['shortname'],
              phone: row['phone'],
              fax: row['fax'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/Branch'),
          ),
        );
      }
    } catch (error) {
      this.logger.log('Error reading local branches from db', error);
    }

    return branches;
  }

  public async reset() {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced branches too.
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localBranchTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localBranchTableDefinition.tableName,
    );
  }

  public async clear() {
    // Only the synced table is cleared here - locally created branches are
    // not pushed to the backend yet, so they must survive a resync.
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.branchTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.branchTableDefinition.tableName,
    );
  }
}
